// src/components/dashboard/widgets/google-reviews-widget.tsx
import { useEffect, useState } from 'react';
import { Star, ExternalLink } from 'lucide-react';
import { supabase } from '@/src/lib/supabase';
import { useAppColors } from '@/src/lib/theme';

interface ReviewsData { rating: number | null; reviewCount: number | null; mapsUri: string | null }

interface GoogleReviewsWidgetProps {
  venueId: string;
}

export default function GoogleReviewsWidget({ venueId }: GoogleReviewsWidgetProps) {
  const c = useAppColors();
  const [data, setData] = useState<ReviewsData | null | undefined>(undefined);

  useEffect(() => {
    supabase.functions.invoke('venue-reviews', { body: { action: 'get', venue_id: venueId } }).then(({ data: d }) => {
      setData(d?.ok ? { rating: d.rating, reviewCount: d.reviewCount, mapsUri: d.mapsUri ?? null } : null);
    });
  }, [venueId]);

  return (
    <div className="rounded-2xl border p-4 bg-warm-gray border-leaf-500/20 h-full">
      <div className="flex items-center justify-between mb-3">
        <div className="w-8 h-8 rounded-lg bg-leaf-500/10 flex items-center justify-center">
          <Star size={15} className="text-leaf-400" />
        </div>
        <span className="text-[9px] font-bold tracking-widest text-cream-muted/50 uppercase">Google Reviews</span>
      </div>
      {data === undefined ? (
        <div className="text-xs text-cream-muted/50">Loading…</div>
      ) : !data || data.rating == null ? (
        <div className="text-xs text-cream-muted/50">No rating found yet</div>
      ) : (
        <>
          <div className="text-2xl font-semibold text-cream leading-none mb-1 flex items-center gap-1.5">
            {data.rating.toFixed(1)}
            <Star size={16} className="text-amber-400 fill-amber-400" />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-cream-muted">{data.reviewCount?.toLocaleString() ?? 0} reviews</span>
            {data.mapsUri && (
              <a href={data.mapsUri} target="_blank" rel="noreferrer" className="text-cream-muted/50 hover:text-cream-muted">
                <ExternalLink size={12} />
              </a>
            )}
          </div>
        </>
      )}
    </div>
  );
}